import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ForecastForm from "./ForecastForm";

const Edit = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({});
  const [rows, setRows] = useState([]);
  const [segmentDates, setSegmentDates] = useState([]);
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [resForecast, resProducts] = await Promise.all([
          Http.get(`/demand-forecast/${code}`),
          Http.get("/demand-forecast-products"),
        ]);

        const forecast = resForecast.data;
        const columnCount = Math.max(forecast.segment_dates.length, 1);

        setInputs({
          start_date: forecast.start_date,
          end_date: forecast.end_date,
          forecasting_duration: forecast.forecasting_duration,
        });
        setSegmentDates(
          forecast.segment_dates.length ? forecast.segment_dates : [{ start_date: "", end_date: "" }]
        );

        // Every product stays on the grid; the saved ones come back filled in.
        const saved = {};
        forecast.rows.forEach((row) => {
          saved[row.product_id] = row;
        });

        setRows(
          resProducts.data.map((product) => {
            const match = saved[product.product_id];
            const segments = Array.from({ length: columnCount }, (_, i) =>
              match && match.segments[i] ? match.segments[i].segment_qty : 0
            );

            return {
              ...product,
              weight: match ? match.weight ?? "" : "",
              length: match ? match.length ?? "" : "",
              height: match ? match.height ?? "" : "",
              width: match ? match.width ?? "" : "",
              segments,
            };
          })
        );
      } catch (e) {
        console.error("Fetch error:", e);
        Swal.fire("Error!", e.response?.data?.message || "Failed to load the forecast.", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [code]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleRowChange = (rowIndex, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === rowIndex ? { ...row, [field]: value } : row)));
  };

  const handleSegmentQtyChange = (rowIndex, segIndex, value) => {
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== rowIndex) return row;
        const segments = [...row.segments];
        segments[segIndex] = value;
        return { ...row, segments };
      })
    );
  };

  const handleSegmentDateChange = (index, field, value) => {
    setSegmentDates((prev) => prev.map((segment, i) => (i === index ? { ...segment, [field]: value } : segment)));
  };

  const addSegment = () => {
    setSegmentDates((prev) => [...prev, { start_date: "", end_date: "" }]);
    setRows((prev) => prev.map((row) => ({ ...row, segments: [...row.segments, 0] })));
  };

  const removeSegment = (index) => {
    if (segmentDates.length < 2) {
      Swal.fire("Warning!", "At least one segment column is required.", "warning");
      return;
    }
    setSegmentDates((prev) => prev.filter((_, i) => i !== index));
    setRows((prev) => prev.map((row) => ({ ...row, segments: row.segments.filter((_, i) => i !== index) })));
  };

  const formSubmit = () => {
    const filled = rows.filter((row) => row.segments.some((qty) => Number(qty) > 0));

    if (filled.length < 1) {
      Swal.fire("Warning!", "Please fill in at least one product segment.", "warning");
      return;
    }

    Http.put(`/demand-forecast/${code}`, { ...inputs, segment_dates: segmentDates, rows: filled })
      .then((res) => {
        navigate("/demand-forecast", { state: { message: res.data.message, type: "success" } });
      })
      .catch((e) => {
        setError(e.response?.data?.errors || {});
        if (e.response?.data?.message && !e.response?.data?.errors) {
          Swal.fire("Failed!", e.response.data.message, "error");
        }
      });
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <ForecastForm
        title="Edit Demand Forecast"
        submitLabel="Update Demand Forecast"
        code={code}
        inputs={inputs}
        rows={rows}
        segmentDates={segmentDates}
        loading={loading}
        error={error}
        onInputChange={handleInputChange}
        onRowChange={handleRowChange}
        onSegmentQtyChange={handleSegmentQtyChange}
        onSegmentDateChange={handleSegmentDateChange}
        onAddSegment={addSegment}
        onRemoveSegment={removeSegment}
        onSubmit={formSubmit}
      />
    </div>
  );
};

export default Edit;
